import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';
import { ethers } from 'ethers';
import axios from 'axios';

// V1 API endpoint - update this if you deploy a new backend
const API_ADDRESS = 'https://api.addrs.to';

export const signOut = async () => {
  try {
    await firebase.auth().signOut();
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};

export const signInWithCustomToken = async (token: string) => {
  try {
    const result = await firebase.auth().signInWithCustomToken(token);
    return result.user;
  } catch (error) {
    console.error('Error signing in with custom token:', error);
    throw error;
  }
};

export const signInWithEmail = async (email: string, password: string) => {
  try {
    const result = await firebase.auth().signInWithEmailAndPassword(email, password);
    return result.user;
  } catch (error) {
    console.error('Error signing in:', error);
    throw error;
  }
};

export const signUpWithEmail = async (email: string, password: string) => {
  try {
    const result = await firebase.auth().createUserWithEmailAndPassword(email, password);
    return result.user;
  } catch (error) {
    console.error('Error signing up:', error);
    throw error;
  }
};

export const signInWithGoogle = async () => {
  try {
    const provider = new firebase.auth.GoogleAuthProvider();
    const result = await firebase.auth().signInWithPopup(provider);
    return result.user;
  } catch (error) {
    console.error('Error signing in with Google:', error);
    throw error;
  }
};

export const signInWithWallet = async () => {
  try {
    // Check if MetaMask or any Web3 provider is available
    if (!(window as any).ethereum) {
      throw new Error('No wallet detected. Please install MetaMask or another Web3 wallet.');
    }

    const provider = new ethers.providers.Web3Provider((window as any).ethereum);
    
    // First, try to get already-connected accounts (doesn't trigger popup)
    let accounts = await provider.listAccounts();
    
    // If no accounts connected, request connection (will trigger popup)
    if (!accounts || accounts.length === 0) {
      console.log('No accounts found, requesting connection...');
      accounts = await provider.send('eth_requestAccounts', []);
    } else {
      console.log('Using already-connected account:', accounts[0]);
    }

    if (!accounts || accounts.length === 0) {
      throw new Error('No accounts found in wallet.');
    }

    const address = accounts[0];

    // V1 Implementation: Use backend API for nonce and custom token generation
    try {
      // Step 1: Get nonce from backend
      const nonceResponse = await axios.post(`${API_ADDRESS}/api/getnonce`, {
        address: address
      });

      if (!nonceResponse.data.NONCE) {
        throw new Error('Nonce could not be retrieved from server.');
      }

      // Step 2: Sign the nonce with MetaMask
      const signer = provider.getSigner();
      const signature = await signer.signMessage(nonceResponse.data.NONCE);

      // Step 3: Verify signature and get custom token from backend
      const verifyResponse = await axios.post(`${API_ADDRESS}/api/verify`, {
        address: address,
        signature: signature
      });

      console.log('Verify response:', verifyResponse.data);

      if (verifyResponse.data.ERROR) {
        throw new Error(`API Error: ${verifyResponse.data.ERROR}`);
      }

      if (!verifyResponse.data.TOKEN) {
        throw new Error('Authentication token could not be retrieved from server.');
      }

      // Step 4: Sign in with custom token
      const result = await signInWithCustomToken(verifyResponse.data.TOKEN);

      // Check if user has a handle (existing user vs new user)
      if (result?.uid) {
        const handleSnapshot = await firebase.database().ref(`/users/${result.uid}`).once('value');
        const hasHandle = handleSnapshot.exists() && handleSnapshot.val();

        return { user: result, isNewUser: !hasHandle };
      }

      return { user: result, isNewUser: true };

    } catch (apiError: any) {
      console.error('Backend API error:', apiError.message);
      throw new Error('Authentication failed. Please try again.');
    }
  } catch (error: any) {
    console.error('Error signing in with wallet:', error);

    // Provide user-friendly error messages
    if (error.code === 4001) {
      throw new Error('Wallet connection rejected by user.');
    } else if (error.code === -32002) {
      throw new Error('Wallet connection request already pending. Please check your wallet.');
    }

    throw error;
  }
};
